import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AdminPanel.css';
import API_URL from '../../config';
import GoogleCalendarSetup from './GoogleCalendarSetup';
import PricingCalendar from './PricingCalendar';
import ReservationCard from '../common/ReservationCard';
import ApprovalEmailModal from './ApprovalEmailModal';
import { PASSWORD_RULE, PASSWORD_MIN_LENGTH, validatePassword } from '../../utils/password';

function AdminPanel() {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reservations');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Filter and sort state
  const [sortBy, setSortBy] = useState('reservation_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterYear, setFilterYear] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFamily, setFilterFamily] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    family: '',
    password: '',
    role: 'user'
  });

  // User filter/sort state
  const [userSearch, setUserSearch] = useState('');
  const [userFilterRole, setUserFilterRole] = useState('all');
  const [userFilterFamily, setUserFilterFamily] = useState('all');
  const [userFilterStatus, setUserFilterStatus] = useState('all');
  const [userFilterType, setUserFilterType] = useState('all');
  const [userSortBy, setUserSortBy] = useState('name');
  const [userSortDir, setUserSortDir] = useState('asc');

  // Edit user state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserForm, setEditUserForm] = useState({});

  // Payments import state
  const [importingPayments, setImportingPayments] = useState(false);

  // Approval email modal state
  const [approvalModalReservation, setApprovalModalReservation] = useState(null);

  // Create reservation form state
  const [showCreateReservation, setShowCreateReservation] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [newReservation, setNewReservation] = useState({
    userId: '',
    checkInDate: '',
    checkOutDate: '',
    status: 'pending',
    type: '',
    year: new Date().getFullYear().toString(),
    description: '',
    notes: '',
    isPublic: false,
    notifyCleaning: false,
    reservationNumber: '',
    totalAmount: ''
  });

  const { getAuthHeader, impersonateUser, user: currentUser } = useAuth();
  const navigate = useNavigate();

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/requests`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      // Ensure data is an array (API may return error object)
      setRequests(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      // Ensure data is an array (API may return error object)
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchRequests();
    fetchUsers();
    const interval = setInterval(() => {
      fetchRequests();
      fetchUsers();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchRequests, fetchUsers]);

  // Auto-prefill total amount from pricing table when dates or user change
  useEffect(() => {
    const { checkInDate, checkOutDate, userId } = newReservation;
    if (!userId || !checkInDate || !checkOutDate || checkInDate >= checkOutDate) return;

    let cancelled = false;
    setCalculatingPrice(true);

    const fetchPrice = async () => {
      try {
        // Pricing covers check-in through night before check-out (per-night rates)
        const endDateObj = new Date(checkOutDate + 'T00:00:00');
        endDateObj.setDate(endDateObj.getDate() - 1);
        const endDate = endDateObj.toISOString().split('T')[0];

        const res = await fetch(
          `${API_URL}/api/calendar-pricing?startDate=${checkInDate}&endDate=${endDate}`,
          { headers: getAuthHeader() }
        );
        if (!res.ok || cancelled) return;

        const rows = await res.json();
        if (!Array.isArray(rows) || cancelled) return;

        const selectedUser = users.find(u => String(u.id) === String(userId));
        const isOwner = selectedUser?.owner === true;
        const total = rows.reduce((sum, row) => {
          const rate = isOwner
            ? parseFloat(row.owner_rate || 0)
            : parseFloat(row.renter_rate || 0);
          return sum + rate;
        }, 0);

        if (!cancelled && total > 0) {
          setNewReservation(prev => ({ ...prev, totalAmount: total.toFixed(2) }));
        }
      } catch (err) {
        // silently fail — pricing data may not exist for these dates
      } finally {
        if (!cancelled) setCalculatingPrice(false);
      }
    };

    fetchPrice();
    return () => { cancelled = true; };
  }, [newReservation.checkInDate, newReservation.checkOutDate, newReservation.userId, getAuthHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateUser = async (e) => {
    e.preventDefault();

    const invalid = validatePassword(newUser.password);
    if (invalid) {
      alert(invalid);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(newUser)
      });

      const data = await response.json();

      if (response.ok) {
        alert(`User created successfully!\nEmail: ${data.user.email}\nPassword: ${newUser.password}\n\nPlease share these credentials with the user.`);

        // Reset form
        setNewUser({
          email: '',
          name: '',
          family: '',
          password: '',
          role: 'user'
        });

        // Refresh users list
        fetchUsers();
      } else {
        const detail = data.errors?.map(e => e.msg).join('\n') || data.error;
        alert(`Error: ${detail || 'Failed to create user'}`);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt(`Enter new password (${PASSWORD_RULE}):`);

    if (newPassword === null) return;

    const invalid = validatePassword(newPassword);
    if (invalid) {
      alert(invalid);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ password: newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Password reset successful for ${data.user.email}`);
      } else {
        const detail = data.errors?.map(e => e.msg).join('\n') || data.error;
        alert(`Error: ${detail || 'Failed to reset password'}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleImpersonate = async (userId, userName) => {
    if (!window.confirm(`View the app as "${userName}"? You can return to your admin account anytime.`)) {
      return;
    }

    const result = await impersonateUser(userId);
    if (result.success) {
      // Reload to show the impersonated user's view
      window.location.reload();
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  const handleEditUser = (user) => {
    setEditingUserId(user.id);
    setEditUserForm({
      name: user.name || '',
      family: user.family || '',
      role: user.role || 'user',
      is_active: user.is_active
    });
  };

  const handleSaveUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(editUserForm)
      });

      const data = await response.json();

      if (response.ok) {
        setEditingUserId(null);
        fetchUsers();
      } else {
        alert(`Error: ${data.error || 'Failed to update user'}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleCancelEditUser = () => {
    setEditingUserId(null);
    setEditUserForm({});
  };

  // Derived: unique families and types for filter dropdowns
  const userFamilies = [...new Set(users.map(u => u.family).filter(Boolean))].sort();
  const userTypes = [...new Set(users.map(u => u.type).filter(Boolean))].sort();

  // Derived: filtered and sorted users
  const filteredUsers = users
    .filter(u => {
      if (userSearch) {
        const q = userSearch.toLowerCase();
        if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
      }
      if (userFilterRole !== 'all' && u.role !== userFilterRole) return false;
      if (userFilterFamily !== 'all' && (u.family || '') !== userFilterFamily) return false;
      if (userFilterStatus !== 'all') {
        const isActive = userFilterStatus === 'active';
        if (u.is_active !== isActive) return false;
      }
      if (userFilterType !== 'all' && (u.type || '') !== userFilterType) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (userSortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'family':
          aVal = (a.family || '').toLowerCase();
          bVal = (b.family || '').toLowerCase();
          break;
        case 'role':
          aVal = a.role || '';
          bVal = b.role || '';
          break;
        case 'last_login':
          aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
          bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        default:
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
      }
      if (aVal < bVal) return userSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return userSortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleUserSort = (field) => {
    if (userSortBy === field) {
      setUserSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortBy(field);
      setUserSortDir('asc');
    }
  };

  const userSortIndicator = (field) => {
    if (userSortBy !== field) return '';
    return userSortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const handleExportPayments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/export`, {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to export payments');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payments.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting payments:', error);
      alert('Failed to export payments');
    }
  };

  const handleImportPayments = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportingPayments(true);

    try {
      const text = await file.text();

      const response = await fetch(`${API_URL}/api/payments/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ csv: text })
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Import complete!\n\nImported: ${data.imported}\nUpdated: ${data.updated}\nSkipped: ${data.skipped}${data.errors ? '\n\nErrors:\n' + data.errors.join('\n') : ''}`);
        fetchRequests(); // Refresh to show updated payment data
      } else {
        alert(`Import failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Error importing payments:', error);
      alert('Failed to import payments');
    } finally {
      setImportingPayments(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();

    if (!newReservation.userId || !newReservation.checkInDate || !newReservation.checkOutDate) {
      alert('Please fill in user, check-in date, and check-out date.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reservations/admin-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(newReservation)
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Reservation created successfully! (${data.request.status})`);
        setNewReservation({
          userId: '',
          checkInDate: '',
          checkOutDate: '',
          status: 'pending',
          type: '',
          year: new Date().getFullYear().toString(),
          description: '',
          notes: '',
          isPublic: false,
          notifyCleaning: false,
          reservationNumber: '',
          totalAmount: ''
        });
        setShowCreateReservation(false);
        fetchRequests();
      } else {
        alert(`Error: ${data.error || 'Failed to create reservation'}`);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      alert('Failed to create reservation');
    }
  };

  const handleApprove = (id) => {
    const reservation = requests.find(r => r.id === id);
    setApprovalModalReservation(reservation || { id });
  };

  const doApprove = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/approve/${id}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        setRequests(requests.map(r => r.id === id ? { ...r, status: 'approved' } : r));
        setApprovalModalReservation(null);
        alert('✅ Reservation approved!');
      } else if (data.code === 'DATE_CONFLICT') {
        const details = (data.conflicts || [])
          .map(c => `#${c.reservation_number} (${c.email}): ${new Date(c.check_in_date).toLocaleDateString()} – ${new Date(c.check_out_date).toLocaleDateString()}`)
          .join('\n');
        alert(`⛔ Cannot approve — these dates are already booked.\n\n${details}`);
      } else {
        alert(`⚠️ ${data.error || 'Failed to approve reservation'}`);
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('⚠️ Failed to approve reservation');
    }
  };

  const handleReject = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/reject/${id}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        setRequests(requests.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
        alert('❌ Reservation rejected!');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleCancel = async (id) => {
    const reason = prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // User clicked cancel

    try {
      const response = await fetch(`${API_URL}/api/reservations/cancel/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        setRequests(requests.map(r => r.id === id ? { ...r, status: 'cancelled' } : r));
        alert('🚫 Reservation cancelled!');
      }
    } catch (error) {
      console.error('Error cancelling request:', error);
    }
  };

  const handleUpdateReservation = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        const user_name = users.find(u => u.email === data.request.email)?.name || null;
        setRequests(prev => prev.map(r => r.id === id
          ? { ...r, ...data.request, user_name, payments: r.payments, total_price: r.total_price, amount_paid: r.amount_paid, amount_owed: r.amount_owed, pending_payments_total: r.pending_payments_total, pending_payments_count: r.pending_payments_count }
          : r
        ));
      } else if (data.code === 'DATE_CONFLICT') {
        const details = (data.conflicts || [])
          .map(c => `#${c.reservation_number} (${c.email}): ${new Date(c.check_in_date).toLocaleDateString()} – ${new Date(c.check_out_date).toLocaleDateString()}`)
          .join('\n');
        alert(`⛔ Cannot save — these dates are already booked.\n\n${details}`);
      } else {
        const detail = data.errors?.map(e => e.msg).join('\n') || data.error;
        alert(`⚠️ ${detail || 'Failed to update reservation'}`);
      }
    } catch (error) {
      console.error('Error updating reservation:', error);
      alert('⚠️ Failed to update reservation');
    }
  };

  // Payment handlers
  const handleAddPayment = async (reservationId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/reservation/${reservationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests(); // Refresh to get updated payment data
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to add payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment');
      return { success: false };
    }
  };

  const handleUpdatePayment = async (paymentId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests(); // Refresh to get updated payment data
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to update payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Failed to update payment');
      return { success: false };
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests(); // Refresh to get updated payment data
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to delete payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment');
      return { success: false };
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/verify`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests(); // Refresh to get updated payment data
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to verify payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert('Failed to verify payment');
      return { success: false };
    }
  };

  const handleRejectPayment = async (paymentId, reason) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        fetchRequests(); // Refresh to get updated payment data
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to reject payment'}`);
        return { success: false };
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('Failed to reject payment');
      return { success: false };
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  
  // Advanced filtering and sorting for approved requests
  const approvedRequests = requests
    .filter(r => r.status === 'approved')
    .filter(r => {
      // Year filter
      if (filterYear !== 'all' && String(r.year) !== filterYear) return false;
      
      // Type filter
      if (filterType !== 'all' && r.type !== filterType) return false;
      
      // Family filter (extract family from email domain or description)
      if (filterFamily !== 'all') {
        const emailDomain = r.email?.toLowerCase() || '';
        const description = r.description?.toLowerCase() || '';
        const familyLower = filterFamily.toLowerCase();
        if (!emailDomain.includes(familyLower) && !description.includes(familyLower)) return false;
      }
      
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchableText = [
          r.reservation_number || '',
          r.description || '',
          r.notes || '',
          new Date(r.check_in_date).toLocaleDateString(),
          new Date(r.check_out_date).toLocaleDateString(),
          r.email || ''
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(query)) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      let compareValue = 0;
      
      switch(sortBy) {
        case 'reservation_number':
          compareValue = (a.reservation_number || '').localeCompare(b.reservation_number || '');
          break;
        case 'check_in_date':
          compareValue = new Date(a.check_in_date) - new Date(b.check_in_date);
          break;
        case 'year':
          compareValue = (a.year || 0) - (b.year || 0);
          break;
        case 'type':
          compareValue = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          compareValue = 0;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  
  const rejectedRequests = requests.filter(r => r.status === 'rejected');
  const cancelledRequests = requests.filter(r => r.status === 'cancelled');

  if (loading) return <div>Loading...</div>;

  return (
    <div className="admin-panel">
      <h2>Admin Dashboard</h2>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === 'reservations' ? 'active' : ''}`}
          onClick={() => setActiveTab('reservations')}
        >
          Reservations
        </button>
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          Pricing
        </button>
        <button
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments
        </button>
        <button
          className="tab-btn"
          onClick={() => navigate('/admin/email')}
        >
          Email
        </button>
        <button
          className="tab-btn"
          onClick={() => navigate('/admin/calendar-email')}
        >
          Calendar Emails
        </button>
        <button
          className="tab-btn"
          onClick={() => navigate('/admin/vacancy-email')}
        >
          Open Weekends
        </button>
      </div>

      {activeTab === 'reservations' && (
        <div className="reservations-tab">
          <GoogleCalendarSetup />

          <div className="create-reservation-section">
            <button
              className="btn btn-primary"
              onClick={async () => {
                if (!showCreateReservation) {
                  try {
                    const res = await fetch(`${API_URL}/api/reservations/next-number`, {
                      headers: getAuthHeader()
                    });
                    const data = await res.json();
                    if (res.ok) {
                      setNewReservation(prev => ({ ...prev, reservationNumber: data.reservationNumber }));
                    }
                  } catch (_) {}
                }
                setShowCreateReservation(!showCreateReservation);
              }}
            >
              {showCreateReservation ? '− Cancel' : '+ Create Reservation'}
            </button>

            {showCreateReservation && (
              <form className="create-reservation-form" onSubmit={handleCreateReservation}>
                <div className="form-row">
                  <div className="form-group">
                    <label>User *</label>
                    <select
                      value={newReservation.userId}
                      onChange={(e) => setNewReservation({ ...newReservation, userId: e.target.value })}
                      required
                    >
                      <option value="">Select a user...</option>
                      {users.filter(u => u.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={newReservation.status}
                      onChange={(e) => setNewReservation({ ...newReservation, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in Date *</label>
                    <input
                      type="date"
                      value={newReservation.checkInDate}
                      onChange={(e) => setNewReservation({ ...newReservation, checkInDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Check-out Date *</label>
                    <input
                      type="date"
                      value={newReservation.checkOutDate}
                      onChange={(e) => setNewReservation({ ...newReservation, checkOutDate: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Reservation Number</label>
                    <input
                      type="text"
                      value={newReservation.reservationNumber}
                      onChange={(e) => setNewReservation({ ...newReservation, reservationNumber: e.target.value })}
                      placeholder="e.g. RES-2026-0001"
                    />
                  </div>
                  <div className="form-group">
                    <label>Total Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newReservation.totalAmount}
                      onChange={(e) => setNewReservation({ ...newReservation, totalAmount: e.target.value })}
                      placeholder={calculatingPrice ? 'Calculating...' : 'e.g. 750.00'}
                      disabled={calculatingPrice}
                    />
                    {calculatingPrice && (
                      <small style={{ color: '#888' }}>Fetching rate from pricing calendar...</small>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <input
                      type="text"
                      value={newReservation.type}
                      onChange={(e) => setNewReservation({ ...newReservation, type: e.target.value })}
                      placeholder="e.g. Individual, Family, Renter"
                    />
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="number"
                      value={newReservation.year}
                      onChange={(e) => setNewReservation({ ...newReservation, year: e.target.value })}
                      placeholder="e.g. 2026"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newReservation.description}
                    onChange={(e) => setNewReservation({ ...newReservation, description: e.target.value })}
                    placeholder="Reservation description..."
                    rows="2"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newReservation.notes}
                    onChange={(e) => setNewReservation({ ...newReservation, notes: e.target.value })}
                    placeholder="Internal notes..."
                    rows="2"
                  />
                </div>
                <div className="form-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newReservation.isPublic}
                      onChange={(e) => setNewReservation({ ...newReservation, isPublic: e.target.checked })}
                    />
                    Public
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newReservation.notifyCleaning}
                      onChange={(e) => setNewReservation({ ...newReservation, notifyCleaning: e.target.checked })}
                    />
                    Notify Cleaning
                  </label>
                </div>
                <button type="submit" className="btn btn-success">Create Reservation</button>
              </form>
            )}
          </div>

          <div className="admin-stats">
            <div className="stat-card">
              <div className="stat-number">{pendingRequests.length}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card approved">
              <div className="stat-number">{approvedRequests.length}</div>
              <div className="stat-label">Approved</div>
            </div>
            <div className="stat-card rejected">
              <div className="stat-number">{rejectedRequests.length}</div>
              <div className="stat-label">Rejected</div>
            </div>
            <div className="stat-card cancelled">
              <div className="stat-number">{cancelledRequests.length}</div>
              <div className="stat-label">Cancelled</div>
            </div>
          </div>

          <div className="requests-section">
            <h3>📋 Pending Requests</h3>
            {pendingRequests.length === 0 ? (
              <p className="no-requests">No pending requests</p>
            ) : (
              <div className="requests-list">
                {pendingRequests.map(req => (
                  <div key={req.id} className="request-card pending">
                    <div className="request-info">
                      {req.user_name && <p><strong>Name:</strong> {req.user_name}</p>}
                      <p><strong>Email:</strong> {req.email}</p>
                      <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                      <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                      {req.description && <p><strong>Description:</strong> {req.description}</p>}
                      {req.notes && <p><strong>Notes:</strong> {req.notes}</p>}
                      {req.notify_cleaning && <p><strong>Cleaning:</strong> Requested</p>}
                      <p><strong>Requested:</strong> {new Date(req.created_at).toLocaleDateString()}</p>
                      <div className="editable-fields">
                        <input
                          type="number"
                          placeholder="Year"
                          defaultValue={req.year || ''}
                          onBlur={(e) => handleUpdateReservation(req.id, { year: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Type"
                          defaultValue={req.type || ''}
                          onBlur={(e) => handleUpdateReservation(req.id, { type: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="request-actions">
                      <button
                        className="btn btn-success"
                        onClick={() => handleApprove(req.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReject(req.id)}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="requests-section">
            <h3>✅ Approved Reservations</h3>
            
            {/* Filter and Sort Controls */}
            <div className="filter-controls">
              <div className="filter-row">
                <div className="sort-controls">
                  <label className="filter-label">Sort by:</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="filter-select"
                  >
                    <option value="reservation_number">Reservation Number</option>
                    <option value="check_in_date">Check-in Date</option>
                    <option value="year">Year</option>
                    <option value="type">Type</option>
                  </select>
                  <button
                    onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                    className="btn btn-primary"
                    title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {sortDirection === 'asc' ? '↑' : '↓'} {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                  </button>
                </div>
              </div>
              
              <div className="filter-row">
                <label className="filter-label">Filter:</label>
                <select 
                  value={filterYear} 
                  onChange={(e) => setFilterYear(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Years</option>
                  {[...new Set(requests.filter(r => r.status === 'approved').map(r => r.year).filter(Boolean))]
                    .sort((a, b) => b - a)
                    .map(year => <option key={year} value={year}>{year}</option>)}
                </select>
                
                <select 
                  value={filterType} 
                  onChange={(e) => setFilterType(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Types</option>
                  {[...new Set(requests.filter(r => r.status === 'approved').map(r => r.type).filter(Boolean))]
                    .sort()
                    .map(type => <option key={type} value={type}>{type}</option>)}
                </select>
                
                <select
                  value={filterFamily}
                  onChange={(e) => setFilterFamily(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Families</option>
                  {[...new Set(users.map(u => u.family).filter(Boolean))]
                    .sort()
                    .map(family => <option key={family} value={family}>{family}</option>)}
                </select>
                
                <input
                  type="text"
                  placeholder="🔍 Search reservations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              {(filterYear !== 'all' || filterType !== 'all' || filterFamily !== 'all' || searchQuery) && (
                <div className="filter-summary">
                  <span>Showing {approvedRequests.length} of {requests.filter(r => r.status === 'approved').length} reservations</span>
                  <button
                    onClick={() => {
                      setFilterYear('all');
                      setFilterType('all');
                      setFilterFamily('all');
                      setSearchQuery('');
                    }}
                    className="btn btn-neutral"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
            
            {approvedRequests.length === 0 ? (
              <p className="no-requests">No approved reservations</p>
            ) : (
              <div className="requests-list">
                {approvedRequests.map(req => (
                  <ReservationCard
                    key={req.id}
                    reservation={{ ...req, user_name: req.user_name || users.find(u => u.email === req.email)?.name }}
                    isEditing={editingId === req.id}
                    editForm={editForm}
                    users={users}
                    onEdit={() => {
                      setEditingId(req.id);
                      setEditForm({
                        reservation_number: req.reservation_number || '',
                        check_in_date: req.check_in_date?.split('T')[0] || '',
                        check_out_date: req.check_out_date?.split('T')[0] || '',
                        year: req.year || '',
                        type: req.type || '',
                        description: req.description || '',
                        notes: req.notes || '',
                        status: req.status,
                        total_amount: req.total_amount != null ? String(req.total_amount) : '',
                        email: req.email || ''
                      });
                    }}
                    onSave={() => {
                      handleUpdateReservation(req.id, editForm);
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                    onChange={(updates) => setEditForm({...editForm, ...updates})}
                    canEditPayments={true}
                    canDeletePayments={true}
                    getAuthHeader={getAuthHeader}
                    onAddPayment={handleAddPayment}
                    onUpdatePayment={handleUpdatePayment}
                    onDeletePayment={handleDeletePayment}
                    onVerifyPayment={handleVerifyPayment}
                    onRejectPayment={handleRejectPayment}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="requests-section">
            <h3>❌ Rejected Requests</h3>
            {rejectedRequests.length === 0 ? (
              <p className="no-requests">No rejected requests</p>
            ) : (
              <div className="requests-list">
                {rejectedRequests.map(req => (
                  <div key={req.id} className="request-card rejected">
                    <div className="request-info">
                      {req.user_name && <p><strong>Name:</strong> {req.user_name}</p>}
                      <p><strong>Email:</strong> {req.email}</p>
                      <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                      <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                      {req.description && <p><strong>Description:</strong> {req.description}</p>}
                      {req.notes && <p><strong>Notes:</strong> {req.notes}</p>}
                      {req.notify_cleaning && <p><strong>Cleaning:</strong> Requested</p>}
                      <p><strong>Rejected:</strong> {req.rejected_at ? new Date(req.rejected_at).toLocaleDateString() : 'N/A'}</p>
                      <div className="editable-fields">
                        <input
                          type="number"
                          placeholder="Year"
                          defaultValue={req.year || ''}
                          onBlur={(e) => handleUpdateReservation(req.id, { year: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Type"
                          defaultValue={req.type || ''}
                          onBlur={(e) => handleUpdateReservation(req.id, { type: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="requests-section">
            <h3>🚫 Cancelled Reservations</h3>
            {cancelledRequests.length === 0 ? (
              <p className="no-requests">No cancelled reservations</p>
            ) : (
              <div className="requests-list">
                {cancelledRequests.map(req => (
                  <div key={req.id} className="request-card cancelled">
                    <div className="request-info">
                      {req.user_name && <p><strong>Name:</strong> {req.user_name}</p>}
                      <p><strong>Email:</strong> {req.email}</p>
                      <p><strong>Check-in:</strong> {new Date(req.check_in_date).toLocaleString()}</p>
                      <p><strong>Check-out:</strong> {new Date(req.check_out_date).toLocaleString()}</p>
                      {req.description && <p><strong>Description:</strong> {req.description}</p>}
                      {req.notes && <p><strong>Notes:</strong> {req.notes}</p>}
                      {req.cancellation_reason && <p><strong>Cancellation Reason:</strong> {req.cancellation_reason}</p>}
                      <p><strong>Cancelled:</strong> {req.cancelled_at ? new Date(req.cancelled_at).toLocaleDateString() : 'N/A'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-tab">
          <div className="user-creation-section">
            <h3>Create New User</h3>
            <form onSubmit={handleCreateUser} className="user-form">
              <div className="form-row">
                <input
                  type="email"
                  placeholder="Email*"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                />
                <input
                  type="text"
                  placeholder="Name*"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Family (optional)"
                  value={newUser.family}
                  onChange={(e) => setNewUser({...newUser, family: e.target.value})}
                />
                <input
                  type="password"
                  placeholder={`Password* (${PASSWORD_RULE})`}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                />
              </div>
              <div className="form-row">
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                >
                  <option value="user">Regular User</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>

          <div className="users-list-section">
            <h3>All Users ({filteredUsers.length}{filteredUsers.length !== users.length ? ` of ${users.length}` : ''})</h3>

            <div className="user-filters">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="user-search-input"
              />
              <div className="user-filter-row">
                <select value={userFilterRole} onChange={(e) => setUserFilterRole(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
                <select value={userFilterFamily} onChange={(e) => setUserFilterFamily(e.target.value)}>
                  <option value="all">All Families</option>
                  {userFamilies.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <select value={userFilterType} onChange={(e) => setUserFilterType(e.target.value)}>
                  <option value="all">All Types</option>
                  {userTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select value={userFilterStatus} onChange={(e) => setUserFilterStatus(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {(userSearch || userFilterRole !== 'all' || userFilterFamily !== 'all' || userFilterType !== 'all' || userFilterStatus !== 'all') && (
                  <button
                    className="btn btn-sm btn-neutral"
                    onClick={() => {
                      setUserSearch('');
                      setUserFilterRole('all');
                      setUserFilterFamily('all');
                      setUserFilterType('all');
                      setUserFilterStatus('all');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th className="sortable-th" onClick={() => handleUserSort('email')}>Email{userSortIndicator('email')}</th>
                    <th className="sortable-th" onClick={() => handleUserSort('name')}>Name{userSortIndicator('name')}</th>
                    <th className="sortable-th" onClick={() => handleUserSort('family')}>Family{userSortIndicator('family')}</th>
                    <th className="sortable-th" onClick={() => handleUserSort('role')}>Role{userSortIndicator('role')}</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th className="sortable-th" onClick={() => handleUserSort('last_login')}>Last Login{userSortIndicator('last_login')}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className={editingUserId === user.id ? 'editing' : ''}>
                      <td>{user.email}</td>
                      <td>
                        {editingUserId === user.id ? (
                          <input
                            type="text"
                            value={editUserForm.name}
                            onChange={(e) => setEditUserForm({...editUserForm, name: e.target.value})}
                            className="edit-input"
                          />
                        ) : (
                          user.name
                        )}
                      </td>
                      <td>
                        {editingUserId === user.id ? (
                          <input
                            type="text"
                            value={editUserForm.family}
                            onChange={(e) => setEditUserForm({...editUserForm, family: e.target.value})}
                            className="edit-input"
                            placeholder="Family"
                          />
                        ) : (
                          user.family || '-'
                        )}
                      </td>
                      <td>
                        {editingUserId === user.id ? (
                          <select
                            value={editUserForm.role}
                            onChange={(e) => setEditUserForm({...editUserForm, role: e.target.value})}
                            className="edit-select"
                          >
                            <option value="user">user</option>
                            <option value="manager">manager</option>
                            <option value="admin">admin</option>
                          </select>
                        ) : (
                          <span className={`role-badge ${user.role}`}>
                            {user.role}
                          </span>
                        )}
                      </td>
                      <td>{user.type || '-'}</td>
                      <td>
                        {editingUserId === user.id ? (
                          <select
                            value={editUserForm.is_active ? 'true' : 'false'}
                            onChange={(e) => setEditUserForm({...editUserForm, is_active: e.target.value === 'true'})}
                            className="edit-select"
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        ) : (
                          <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="user-actions">
                        {editingUserId === user.id ? (
                          <>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleSaveUser(user.id)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm btn-neutral"
                              onClick={handleCancelEditUser}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleResetPassword(user.id)}
                            >
                              Reset PW
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                className="btn btn-sm btn-accent"
                                onClick={() => handleImpersonate(user.id, user.name || user.email)}
                                title="View app as this user"
                              >
                                View As
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="pricing-tab">
          <PricingCalendar />
        </div>
      )}

      {approvalModalReservation && (
        <ApprovalEmailModal
          reservation={approvalModalReservation}
          onClose={() => setApprovalModalReservation(null)}
          onApprove={doApprove}
        />
      )}

      {activeTab === 'payments' && (
        <div className="payments-tab">
          <div className="payments-management">
            <h3>Payment Management</h3>
            <p className="payments-description">
              Export payments to CSV, edit them in a spreadsheet, and import the changes back.
            </p>

            <div className="payments-actions">
              <button
                className="btn btn-primary"
                onClick={handleExportPayments}
              >
                Export Payments (CSV)
              </button>

              <label className="btn btn-success file-input-label">
                {importingPayments ? 'Importing...' : 'Import Payments (CSV)'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportPayments}
                  disabled={importingPayments}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            <div className="payments-help">
              <h4>CSV Format</h4>
              <p>The CSV file should have the following columns:</p>
              <ul>
                <li><strong>reservation_id</strong> (required) - The ID of the reservation</li>
                <li><strong>amount</strong> (required) - Payment amount (e.g., 150.00)</li>
                <li><strong>payment_date</strong> (required) - Date in YYYY-MM-DD format</li>
                <li><strong>payment_method</strong> (optional) - e.g., Check, Cash, Venmo</li>
                <li><strong>notes</strong> (optional) - Any notes about the payment</li>
                <li><strong>id</strong> (optional) - Include to update an existing payment</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
